// apps/client/src/net/actions.ts — reqIds, optimistic undo, timeouts, toasts on reject (§4.7)
import { t, tuning, type StringKey, type StringParams } from '@whiskerton/content';
import type { AckOk, AckReject, ActionReq, ActionRes, ActionType, PrivatePatch, RejectCode } from '@whiskerton/shared';

export type ActResult<T extends ActionType> = { ok: true; r: ActionRes<T> } | { ok: false; code: RejectCode | 'offline' | 'timeout' };
export interface ActionDeps {
  send(frame: { t: 'act'; id: number; a: string; p: unknown }): boolean; online(): boolean; resync(): void;
  toast(key: StringKey, params?: StringParams): void; onPriv(patch: PrivatePatch): void;
}
/** No reject toast: `true` for none at all, or the reject codes the caller answers itself (every other code still toasts). */
export type Silent = boolean | readonly RejectCode[];
type Pending = { resolve: (r: ActResult<ActionType>) => void; undo: () => void; timer: ReturnType<typeof setTimeout>; silent: Silent };

export class ActionClient {
  private readonly deps: ActionDeps;
  private readonly pending = new Map<number, Pending>();
  private seq = 0;
  constructor(deps: ActionDeps) { this.deps = deps; }
  /** Runs `optimistic` at once (it returns its undo), sends the action and resolves on the ack. A reject, a timeout or going offline
   *  undoes the optimistic step; a reject also toasts `reject.<code>` unless `silent` covers it, and a timeout asks for a fresh snapshot. */
  send<T extends ActionType>(type: T, req: ActionReq<T>, opts: { optimistic?: () => () => void; silent?: Silent } = {}): Promise<ActResult<T>> {
    const undo = opts.optimistic?.() ?? (() => {});
    const id = ++this.seq;
    if (!this.deps.online() || !this.deps.send({ t: 'act', id, a: type, p: req })) { undo(); return Promise.resolve({ ok: false, code: 'offline' }); }
    return new Promise((resolve) => {
      const timer = setTimeout(() => { this.pending.delete(id); undo(); this.deps.resync(); resolve({ ok: false, code: 'timeout' }); }, tuning.net.actionTimeoutMs);
      this.pending.set(id, { resolve: resolve as Pending['resolve'], undo, timer, silent: opts.silent ?? false });
    });
  }
  onAck(ack: AckOk | AckReject): void {
    const p = this.pending.get(ack.id);
    if (!p) return;
    this.pending.delete(ack.id);
    clearTimeout(p.timer);
    if (ack.ok) { if (ack.priv) this.deps.onPriv(ack.priv); p.resolve({ ok: true, r: ack.r }); return; }
    p.undo();
    const quiet = p.silent === true || (Array.isArray(p.silent) && p.silent.includes(ack.code));
    if (!quiet) this.deps.toast(`reject.${ack.code}` as StringKey, ack.code === 'not_enough_bells' ? { currency: t('common.currencyPlural') } : undefined);
    p.resolve({ ok: false, code: ack.code });
  }
  /** The socket closed: every pending action resolves `offline` and is undone. */
  failAll(): void {
    for (const [id, p] of this.pending) { clearTimeout(p.timer); p.undo(); p.resolve({ ok: false, code: 'offline' }); this.pending.delete(id); }
  }
}
